import React, { memo } from 'react';
import { View, StyleSheet, ActivityIndicator, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

interface ActionButtonsProps {
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onDelete?: () => void;
  submitText?: string;
  showDelete?: boolean;
  hideSubmit?: boolean;
  cancelText?: string;
  deleteText?: string;
  /** Override container styles (e.g. to switch from absolute to flow layout) */
  containerStyle?: ViewStyle;
}

export const ActionButtons = memo<ActionButtonsProps>(({
  isLoading,
  onCancel,
  onSubmit,
  onDelete,
  submitText = 'Submit',
  showDelete = false,
  hideSubmit = false,
  cancelText = 'Cancel',
  deleteText = 'Delete',
  containerStyle,
}) => {
  const styles = useStyles();
  const insets = useSafeAreaInsets();

  return (
    <View style={[styles.actionButtons, { paddingBottom: 16 + insets.bottom }, containerStyle]}>
      {showDelete && onDelete && (
        <Hoverable
          hover="row"
          style={styles.deleteButton}
          onPress={onDelete}
          disabled={isLoading}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          testID="action-delete"
        >
          <Text style={styles.deleteButtonText}>{deleteText}</Text>
        </Hoverable>
      )}

      <Hoverable
        hover="glass"
        style={styles.cancelButton}
        onPress={onCancel}
        disabled={isLoading}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        testID="action-cancel"
      >
        <Text style={styles.cancelButtonText}>{cancelText}</Text>
      </Hoverable>

      {!hideSubmit && (
        <Hoverable
          hover="button"
          style={[styles.submitButton, isLoading && styles.submitButtonDisabled]}
          onPress={onSubmit}
          disabled={isLoading}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          testID="action-submit"
        >
          {isLoading ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.submitButtonText}>{submitText}</Text>
          )}
        </Hoverable>
      )}
    </View>
  );
});

ActionButtons.displayName = 'ActionButtons';

const useStyles = () =>
  useThemedStyles(({ components, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      actionButtons: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: admin.dataTable.bg,
        borderTopWidth: 1,
        borderTopColor: admin.dataTable.border,
        padding: 16,
        flexDirection: 'row',
        gap: 12,
        zIndex: 100,
      },
      deleteButton: {
        flex: 1,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: semantic.status.error,
        borderRadius: 8,
        paddingVertical: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 48,
      },
      deleteButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: semantic.status.error,
      },
      cancelButton: {
        flex: 1,
        backgroundColor: 'transparent',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
        paddingVertical: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 48,
      },
      cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: admin.dataTable.text,
      },
      submitButton: {
        flex: 1,
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
        borderRadius: 8,
        paddingVertical: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 48,
      },
      submitButtonDisabled: {
        opacity: 0.6,
      },
      submitButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#FFFFFF',
      },
    });
  });
